import type { MarketingTranslator } from '@/i18n/marketing'
import { useMemo, useState } from 'react'
import { clsx } from 'clsx'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'

import { CtaButton } from './CtaButton'
import {
  InquiryDialog,
  Field,
  CheckCircle,
  inputClass,
  RegionPicker,
  ConsentSection,
} from './inquiry'
import {
  getBriefingInterests,
  INTEREST_OTHER_VALUE,
  getFranchiseTypes,
} from '@/pages/home/briefing.content'
import { lcMarketingApi } from '@/api/lcMarketing'
import { trackNaverConversion } from '@/lib/naver'

function getApiErrorCode(error: unknown): string | undefined {
  return (
    error as {
      response?: { data?: { error?: { code?: string } | null } }
    }
  )?.response?.data?.error?.code
}

export function formatSessionLabel(startsAt: string, language = 'ko'): string {
  const parts = new Intl.DateTimeFormat(language === 'en' ? 'en-US' : 'ko-KR', {
    timeZone: 'Asia/Seoul',
    month: 'numeric',
    day: 'numeric',
    weekday: 'short',
  }).formatToParts(new Date(startsAt))
  const month = parts.find((part) => part.type === 'month')?.value ?? ''
  const day = parts.find((part) => part.type === 'day')?.value ?? ''
  const weekday = parts.find((part) => part.type === 'weekday')?.value ?? ''
  const time = new Intl.DateTimeFormat(language === 'en' ? 'en-US' : 'ko-KR', {
    timeZone: 'Asia/Seoul',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(startsAt))
  return `${month}/${day} (${weekday}) ${time}${language === 'en' ? ' KST' : ''}`
}

function RadioDot({ checked }: { checked: boolean }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center justify-center w-6 h-6 rounded-full shrink-0 border-[1.5px] bg-white transition-colors',
        checked ? 'border-qb-primary' : 'border-[#dadada]',
      )}
      aria-hidden
    >
      {checked && (
        <span className="w-[10px] h-[10px] rounded-full bg-qb-primary" />
      )}
    </span>
  )
}

const phoneRegex = /^(01[016789])([1-9]{1}[0-9]{2,3})([0-9]{4})$/

function createFormSchema(mt: MarketingTranslator) {
  const FormSchema = z
    .object({
      name: z.string().min(1, mt('form.nameRequired')).max(50, mt('form.maxName')),
      phone: z.string().regex(phoneRegex, mt('form.phoneInvalid')),
      sessionMode: z.enum(['offline', 'online']),
      franchiseTypes: z
        .array(z.enum(['academy', 'tutoring']))
        .min(1, mt('form.franchiseTypeRequired')),
      sido: z.string().min(1, mt('form.provinceRequired')),
      sigungu: z.string().min(1, mt('form.districtRequired')),
      sessionId: z.string().uuid(mt('form.sessionRequired')),
      interests: z.array(z.string()).min(1, mt('form.interestRequired')),
      otherText: z.string().max(500, mt('form.maxDetails')).optional().or(z.literal('')),
      privacyConsent: z.literal(true, {
        errorMap: () => ({ message: mt('form.privacyRequired') }),
      }),
      marketingConsent: z.boolean(),
    })
    .refine(
      (d) =>
        !d.interests.includes(INTEREST_OTHER_VALUE) ||
        (d.otherText !== undefined && d.otherText.trim().length > 0),
      { path: ['otherText'], message: mt('form.detailsRequired') },
    )
  return FormSchema
}

type FormValues = z.infer<ReturnType<typeof createFormSchema>>

interface BriefingApplicationModalProps {
  open: boolean
  onClose: () => void
}

export function BriefingApplicationModal({
  open,
  onClose,
}: BriefingApplicationModalProps) {
  const { t: mt, i18n } = useTranslation('marketing')
  const FRANCHISE_TYPES = getFranchiseTypes(mt)
  const BRIEFING_INTERESTS = getBriefingInterests(mt)
  const FormSchema = createFormSchema(mt)

  const [submitted, setSubmitted] = useState(false)
  const queryClient = useQueryClient()
  const { t } = useTranslation('common')

  const { data: sessions = [] } = useQuery({
    queryKey: ['lc-marketing', 'briefing-sessions'],
    queryFn: lcMarketingApi.getBriefingSessions,
    enabled: open,
    staleTime: 30_000,
  })

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      phone: '',
      sessionMode: 'offline',
      franchiseTypes: [],
      sido: '',
      sigungu: '',
      sessionId: '',
      interests: [],
      otherText: '',
      privacyConsent: false as unknown as true,
      marketingConsent: false,
    },
  })

  const sessionMode = watch('sessionMode')
  const sessionId = watch('sessionId')
  const selectedInterests = watch('interests')
  const showOtherInput = selectedInterests?.includes(INTEREST_OTHER_VALUE)

  const sessionsForMode = useMemo(
    () => sessions.filter((session) => session.mode === sessionMode),
    [sessionMode, sessions],
  )

  const submitMutation = useMutation({
    mutationFn: (values: FormValues) =>
      lcMarketingApi.submitBriefingApplication({
        sessionId: values.sessionId,
        name: values.name.trim(),
        phone: values.phone.trim(),
        franchiseTypes: values.franchiseTypes,
        sido: values.sido?.trim() || undefined,
        sigungu: values.sigungu?.trim() || undefined,
        interests: values.interests,
        otherText: values.interests.includes(INTEREST_OTHER_VALUE)
          ? values.otherText?.trim() || undefined
          : undefined,
        privacyConsent: true,
        marketingConsent: values.marketingConsent,
      }),
    onSuccess: () => {
      setSubmitted(true)
      trackNaverConversion('custom003')
      setTimeout(() => {
        handleClose()
      }, 1800)
    },
    onError: (error) => {
      const code = getApiErrorCode(error)
      if (
        code === 'BRIEFING_SESSION_FULL' ||
        code === 'BRIEFING_SESSION_CLOSED' ||
        code === 'BRIEFING_SESSION_NOT_FOUND'
      ) {
        toast.error(t('briefing.sessionUnavailable'))
        queryClient.invalidateQueries({
          queryKey: ['lc-marketing', 'briefing-sessions'],
        })
        setValue('sessionId', '', { shouldValidate: true })
        return
      }
      if (code === 'BRIEFING_DUPLICATE_SESSION') {
        toast.error(t('briefing.duplicateSession'))
        return
      }
      toast.error(t('briefing.submitFailed'))
    },
  })

  const handleClose = () => {
    reset()
    setSubmitted(false)
    onClose()
  }

  const tabs = (
    <div className="flex shrink-0 border-b border-qb-divider">
      {(['offline', 'online'] as const).map((mode) => {
        const active = sessionMode === mode
        return (
          <button
            key={mode}
            type="button"
            onClick={() => {
              setValue('sessionMode', mode, { shouldValidate: true })
              setValue('sessionId', '', { shouldValidate: true })
            }}
            className={clsx(
              'flex-1 flex flex-col items-center justify-center pt-[10px] pb-4 border-b-2',
              active
                ? 'border-qb-primary text-qb-primary font-bold'
                : 'border-qb-divider text-qb-text-mid font-medium',
              'text-[18px] tracking-[-0.6px] leading-[1.5]',
            )}
          >
            {mode === 'offline' ? mt('SalesBriefingSection.DEFAULTS.offline.title_407') : mt('SalesBriefingSection.DEFAULTS.online.title_401')}
          </button>
        )
      })}
    </div>
  )

  const footer = !submitted ? (
    <CtaButton
      type="submit"
      form="briefing-form"
      variant="primary"
      size="lg"
      fullWidth
      disabled={!isValid || submitMutation.isPending}
    >
      {submitMutation.isPending ? mt('form.submitting') : mt('form.register')}
    </CtaButton>
  ) : null

  return (
    <InquiryDialog
      open={open}
      onClose={handleClose}
      title={mt('header.briefing')}
      tabs={tabs}
      footer={footer}
    >
      {submitted ? (
        <div className="py-12 text-center text-qb-body-2 text-qb-text-high">
          {mt('form.applicationReceived')}
        </div>
      ) : (
        <form
          id="briefing-form"
          onSubmit={handleSubmit((v) => submitMutation.mutate(v))}
          className="flex flex-col gap-6 px-8 pt-6 pb-5"
        >
          {i18n.resolvedLanguage === 'en' && (
            <p className="text-sm text-qb-text-mid">{mt('domesticFormNotice')}</p>
          )}
          <Field label={mt('form.name')} error={errors.name?.message}>
            <input
              {...register('name')}
              placeholder={mt('form.nameRequired')}
              className={inputClass}
              autoComplete="name"
            />
          </Field>

          <Field label={mt('form.phone')} error={errors.phone?.message}>
            <input
              {...register('phone')}
              placeholder="01012345678"
              className={inputClass}
              inputMode="tel"
              autoComplete="tel"
            />
          </Field>

          <Field
            label={mt('form.franchiseType')}
            error={(errors.franchiseTypes as { message?: string } | undefined)?.message}
          >
            <Controller
              control={control}
              name="franchiseTypes"
              render={({ field }) => (
                <div className="flex gap-[10px]">
                  {FRANCHISE_TYPES.map((opt) => {
                    const checked = (field.value ?? []).includes(opt.value)
                    return (
                      <button
                        type="button"
                        key={opt.value}
                        onClick={() => {
                          const current = field.value ?? []
                          if (checked) {
                            field.onChange(current.filter((v) => v !== opt.value))
                          } else {
                            field.onChange([...current, opt.value])
                          }
                        }}
                        className={clsx(
                          'flex items-center gap-[10px] h-[51px] px-4 rounded-qb-md border text-[16px] font-medium leading-[1.5] transition-colors flex-1',
                          checked
                            ? 'bg-qb-primary-subtle border-qb-primary text-qb-text-high'
                            : 'bg-qb-bg-secondary border-transparent text-qb-text-mid',
                        )}
                      >
                        <CheckCircle checked={checked} />
                        {opt.label}
                      </button>
                    )
                  })}
                </div>
              )}
            />
          </Field>

          <Field label={mt('form.location')} error={errors.sigungu?.message}>
            <Controller
              control={control}
              name="sido"
              render={({ field: sidoField }) => (
                <Controller
                  control={control}
                  name="sigungu"
                  render={({ field: sigunguField }) => (
                    <RegionPicker
                      sido={sidoField.value || undefined}
                      sigungu={sigunguField.value || undefined}
                      onChange={(next) => {
                        sidoField.onChange(next.sido ?? '')
                        sigunguField.onChange(next.sigungu ?? '')
                      }}
                    />
                  )}
                />
              )}
            />
          </Field>

          <Field label={mt('form.date')} error={errors.sessionId?.message}>
            <div className="flex flex-col gap-[10px] sm:flex-row" role="radiogroup" aria-label={mt('form.date')}>
              {sessionsForMode.map((session) => {
                const closed = session.applicationAvailability !== 'open'
                const checked = sessionId === session.sessionId
                const label = formatSessionLabel(session.startsAt, i18n.resolvedLanguage)
                return (
                  <label
                    key={session.sessionId}
                    className={clsx(
                      'flex items-center gap-[10px] h-[51px] px-4 rounded-qb-md border text-[16px] font-medium leading-[1.5] transition-colors w-full whitespace-nowrap sm:flex-1',
                      closed
                        ? 'bg-qb-bg-secondary border-transparent text-qb-text-low cursor-not-allowed opacity-60'
                        : checked
                          ? 'bg-qb-primary-subtle border-qb-primary text-qb-text-high cursor-pointer'
                          : 'bg-qb-bg-secondary border-transparent text-qb-text-mid cursor-pointer',
                    )}
                  >
                    <input
                      type="radio"
                      {...register('sessionId')}
                      value={session.sessionId}
                      disabled={closed}
                      className="sr-only"
                    />
                    <RadioDot checked={checked} />
                    {closed ? `${label} — ${t('briefing.closed')}` : label}
                  </label>
                )
              })}
            </div>
          </Field>

          <Field
            label={mt('form.interests')}
            error={errors.interests?.message}
          >
            <Controller
              control={control}
              name="interests"
              render={({ field }) => (
                <div className="flex flex-col gap-[10px]">
                  <div className="grid grid-cols-2 gap-[10px]">
                    {BRIEFING_INTERESTS.filter(
                      (opt) => opt.value !== INTEREST_OTHER_VALUE,
                    ).map((opt) => {
                      const checked = field.value.includes(opt.value)
                      return (
                        <button
                          type="button"
                          key={opt.value}
                          onClick={() => {
                            if (checked) {
                              field.onChange(field.value.filter((v) => v !== opt.value))
                            } else {
                              field.onChange([...field.value, opt.value])
                            }
                          }}
                          className={clsx(
                            'flex items-center gap-[10px] h-[51px] px-4 rounded-[12px] border text-[16px] font-medium leading-[1.5] text-left transition-colors',
                            checked
                              ? 'bg-qb-primary-subtle border-qb-primary text-qb-text-high'
                              : 'bg-qb-bg-secondary border-transparent text-qb-text-mid',
                          )}
                        >
                          <CheckCircle checked={checked} />
                          {opt.label}
                        </button>
                      )
                    })}
                  </div>
                  {(() => {
                    const otherOpt = BRIEFING_INTERESTS.find(
                      (o) => o.value === INTEREST_OTHER_VALUE,
                    )!
                    const checked = field.value.includes(INTEREST_OTHER_VALUE)
                    return (
                      <button
                        type="button"
                        onClick={() => {
                          if (checked) {
                            field.onChange(field.value.filter((v) => v !== INTEREST_OTHER_VALUE))
                          } else {
                            field.onChange([...field.value, INTEREST_OTHER_VALUE])
                          }
                        }}
                        className={clsx(
                          'flex items-center gap-[10px] h-[51px] px-4 rounded-qb-md border text-[16px] font-medium leading-[1.5] text-left transition-colors w-full',
                          checked
                            ? 'bg-qb-primary-subtle border-qb-primary text-qb-text-high'
                            : 'bg-qb-bg-secondary border-transparent text-qb-text-mid',
                        )}
                      >
                        <CheckCircle checked={checked} />
                        {otherOpt.label}
                      </button>
                    )
                  })()}
                </div>
              )}
            />
          </Field>

          {showOtherInput && (
            <Field label={mt('form.other')} error={errors.otherText?.message}>
              <input
                {...register('otherText')}
                placeholder={mt('form.questionsPlaceholder')}
                className={inputClass}
              />
            </Field>
          )}

          <Controller
            control={control}
            name="privacyConsent"
            render={({ field: privacy }) => (
              <Controller
                control={control}
                name="marketingConsent"
                render={({ field: marketing }) => (
                  <ConsentSection
                    privacyAgreed={Boolean(privacy.value)}
                    marketingAgreed={marketing.value}
                    onPrivacyChange={(next) => privacy.onChange(next as unknown as true)}
                    onMarketingChange={(next) => marketing.onChange(next)}
                  />
                )}
              />
            )}
          />
        </form>
      )}
    </InquiryDialog>
  )
}
